import React, { useEffect, useContext } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useLazyQuery, useQuery, useMutation } from '@apollo/client';
import rison from 'rison';

import { Root } from './AcademyEducation.style';
import { ProductsGrid } from '../../../components/ProductsGrid/ProductsGrid';
import { ProductsItem } from '../../../components/ProductsItem/ProductsItem';
import { Breadcrumbs } from '../../../components/Breadcrumbs/Breadcrumbs';
import { PageTitle } from '../../../components/PageTitle/PageTitle';
import { FiltersDesktop } from '../../../components/FiltersDesktop/FiltersDesktop';
import { FiltersMobile } from '../../../components/FiltersMobile/FiltersMobile';
import { SortByPanel } from '../../../components/SortByPanel/SortByPanel';
import { Pagination } from '../../../components/Pagination/Pagination';
import { EducationBlock } from '../../../components/EducationBlock/EducationBlock';

import { sortConfig, GET_ACADEMY_TAGS, academyTagPath, GET_EDUCATION_REWARDS } from './config';
import { apiDomain, apiSiteId } from '../../../config';
import { brands } from '../../../config/brands';
import { UserContext } from '../../../context/UserContext';
import {
  GET_WISHLIST,
  ADD_TO_WISHLIST,
  REMOVE_FROM_WISHLIST,
} from '../../RewardsView/rewardsViewConfig';
import { toast, ToastNotification } from '../../../ui/ToastNotification/ToastNotification';

export const AcademyEducation = () => {
  const intl = useIntl();
  const locale = intl.locale;
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useContext(UserContext);

  const [getEducationRewards, { loading, data }] = useLazyQuery(GET_EDUCATION_REWARDS);

  const { data: wishlistData } = useQuery(GET_WISHLIST, {
    skip: user?._id == null,
    variables: { locale },
    fetchPolicy: 'cache-first',
  });

  const [addToWishlist] = useMutation(ADD_TO_WISHLIST, {
    refetchQueries: ['GetWishlist', 'GetHeaderCartData'],
  });

  const [removeFromWishlist] = useMutation(REMOVE_FROM_WISHLIST, {
    refetchQueries: ['GetWishlist', 'GetHeaderCartData'],
  });

  const handleAddToFavourites = async ({ productId, productName, inFavorites }) => {
    if (user?._id == null) {
      return navigate(`/${intl.locale}/auth?redirect=${encodeURIComponent(location.pathname)}`);
    }
    toast.dismiss();
    if (inFavorites) {
      toast.warn(
        <ToastNotification>
          <FormattedMessage
            defaultMessage="{productName} removed from wishlist."
            values={{ productName }}
          />
        </ToastNotification>,
      );
      await removeFromWishlist({ variables: { productId, locale } });
    } else {
      toast.success(
        <ToastNotification>
          <FormattedMessage
            defaultMessage="{productName} added to wishlist."
            values={{ productName }}
          />
        </ToastNotification>,
      );
      await addToWishlist({ variables: { productId, locale } });
    }
  };

  useEffect(() => {
    let filters = searchParams.has('filters')
      ? rison.decode(searchParams.get('filters'))
      : {};

    filters.type = 'education-reward';
    filters.e = true;
    filters = JSON.stringify(filters);

    let sort = searchParams.get('sort');
    sort = sortConfig.find((s) => s.value === sort);
    sort = sort == null ? null : { key: sort.key, direction: sort.dir };

    const page = searchParams.has('page') ? searchParams.get('page') : 1;
    const limit = 200;

    getEducationRewards({
      variables: { sort, page, filters, limit, locale },
      fetchPolicy: 'network-only',
    });
  }, [searchParams, locale, getEducationRewards]);

  const docs = (data?.content?.findClubH?.docs || []).filter(
    (r) => r.data?.category === 'classroom-education',
  );

  const toTitleCase = (str) =>
    str ? str.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) : '';

  return (
    <Root>
      <FiltersMobile
        sortOptions={sortConfig}
        tagsConfig={{ gql: GET_ACADEMY_TAGS, dataPath: academyTagPath }}
        showPoints={true}
        pointsConfig={{ min: null, max: null }}
        searchPlaceholder="Search academy classes"
      />

      <Breadcrumbs
        breadcrumbs={[
          { to: '/', label: intl.formatMessage({ defaultMessage: 'Home' }) },
          { to: '/education', label: intl.formatMessage({ defaultMessage: 'Education' }) },
          { to: null, label: intl.formatMessage({ defaultMessage: 'Academy Education' }) },
        ]}
      />

      <PageTitle title={intl.formatMessage({ defaultMessage: 'ACADEMY EDUCATION' })} />

      <FiltersDesktop
        tagsConfig={{ gql: GET_ACADEMY_TAGS, dataPath: academyTagPath }}
        pointsConfig={{ min: null, max: null }}
        showPoints={true}
        searchPlaceholder="Search academy classes"
      />

      <SortByPanel sortOptions={sortConfig} />

      <ProductsGrid
        columns={3}
        loading={loading}
        totalCount={docs.length}
        onResetFilters={() => {
          searchParams.delete('filters');
          setSearchParams(searchParams);
        }}
      >
        {docs.map((item) => {
          const inFavorites = (wishlistData?.me?.wishlist?.items || []).some(
            (itm) => itm?.product?._id === item._id,
          );

          const imgSrc =
            item?.gallery?.[0]?.media?._id != null
              ? `https://${apiDomain}/${apiSiteId}/asset/${item.gallery[0].media._id}?type=fill&width=604&height=752`
              : null;

          const brandConfig = brands.find((b) => b.id === item.brand?._id);
          const brandName = brandConfig?.label ?? item.brand?.n;
          const brandSlug = brandConfig?.slug;

          const educationData = item.data || {};

          let educationDatetime = null;
          if (educationData.dateTime) {
            const dt = new Date(educationData.dateTime);
            const dateStr = dt.toLocaleDateString(locale === 'fr-CA' ? 'fr-CA' : 'en-CA', {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            });
            const timeStr = dt.toLocaleTimeString(locale === 'fr-CA' ? 'fr-CA' : 'en-CA', {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true,
            });
            educationDatetime = `${dateStr} | ${timeStr}`;
          }

          const rawParts = [educationData.distributor, educationData.city, educationData.province];
          const educationLocation = rawParts
            .map((p, idx) =>
              p
                ? idx === rawParts.length - 1 && p.length === 2
                  ? p.toUpperCase()
                  : toTitleCase(p)
                : null,
            )
            .filter(Boolean)
            .join(', ');

          return (
            <ProductsItem
              key={item._id}
              imageSrc={imgSrc}
              brandName={brandName ?? item.brand?.n}
              brandUrl={brandSlug ? `/brands/${brandSlug}` : '/rewards'}
              brandIcon={brandConfig?.icon}
              productName={item.n}
              productUrl={`/education/class/${item.sl}`}
              pts={item?.ecommerce?.price?.amount}
              oldPts={item?.ecommerce?.fullPrice?.amount}
              inFavorites={inFavorites}
              isEducation={true}
              educationDatetime={educationDatetime}
              educationLocation={educationLocation}
              onAddToFavourites={() =>
                handleAddToFavourites({
                  productId: item._id,
                  productName: item.n,
                  inFavorites,
                })
              }
              onAddToCart={() => {}}
            />
          );
        })}
      </ProductsGrid>

      <Pagination
        totalCount={docs.length}
        currentPage={1}
        pageSize={200}
        onPaginate={(e) => {
          if (e === 1) searchParams.delete('page');
          else searchParams.set('page', e);
          setSearchParams(searchParams);
        }}
      />

      <EducationBlock excludePage="academy-education" />
    </Root>
  );
};
